<template>
  <div class="mt-12 space-y-8">
    <div class="space-y-2">
      <SectionHeader :title="__('Company Details')">
        <template #icon>
          <Icon class="text-3xl text-primary" name="account_box" is-filled />
        </template>
      </SectionHeader>

      <div v-if="props.vettingOn === 1">
        <h2 class="text-lg font-semibold leading-tight">
          {{ __('For any changes to your company details, get in touch with us at [demployment_email_address]') }}
        </h2>
      </div>
    </div>
    <div class="shadow-card rounded-lg bg-white px-4 py-6 md:px-6 md:py-8">
      <form @submit.prevent="handleSubmit" novalidate>
        <div class="grid gap-4 md:grid-cols-2 mb-4">
          <InputText
              id="name"
              v-model:value="values.name"
              v-model:errors="errors.name"
              :label="__('Company Name')"
              :placeholder="__('Add Company Name')"
              :disabled="isFieldDisabled('name')"
              required
          />
          <InputText
              id="website"
              v-model:value="values.website"
              v-model:errors="errors.website"
              :label="__('Company Website')"
              :placeholder="__('Add Company Website')"
              :disabled="isFieldDisabled('website')"
              required
          />
        </div>

        <div class="grid gap-4 md:grid-cols-2">
          <div class="relative">
            <InputText
                id="email"
                v-model:value="values.email"
                v-model:errors="errors.email"
                :label="__('Email')"
                :placeholder="__('Email')"
                :disabled="isFieldDisabled('email')"
                required
            />
            <button
                class="absolute right-0 top-[3px] flex items-center gap-1 text-zinc-600"
                type="button"
                @click="handleVisibilityButtonClick('email')"
            >
              <span class="text-xs">
                {{ values.email_is_visible ? __('Visible to candidates') : __('Not visible to candidates') }}
              </span>
              <Icon
                  class="text-lg"
                  :name="values.email_is_visible ? 'visibility' : 'visibility_off'"
              />
            </button>
          </div>
          <div class="grid grid-cols-1 gap-x-2 gap-y-4 sm:grid-cols-5 mb-4">
            <InputSelect
                id="country_prefix"
                v-model:value="values.phone_code"
                v-model:errors="errors.phone_code"
                :label="__('Country prefix')"
                :placeholder="__('Prefix')"
                :options="options.country_prefix"
                class="col-span-1 sm:col-span-2"
                :disabled="isFieldDisabled('phone_code')"
                required
            />

            <div class="relative col-span-1 sm:col-span-3">
              <InputText
                  id="phone_number"
                  v-model:value="values.phone_number"
                  v-model:errors="errors.phone_number"
                  class="col-span-1 sm:col-span-3"
                  :label="__('Phone Number')"
                  :placeholder="__('754 782 082')"
                  :disabled="isFieldDisabled('phone_number')"
                  required
              >
                <template #icon>
                  <Icon class="text-lg text-neutral-400" name="phone" is-filled />
                </template>
              </InputText>
              <button
                  class="absolute right-0 top-[3px] flex items-center gap-1 text-zinc-600"
                  type="button"
                  @click="handleVisibilityButtonClick('phone_number')"
              >
                <span class="text-xs">
                  {{ values.phone_is_visible ? __('Visible to candidates') : __('Not visible to candidates') }}
                </span>
                <Icon
                    class="text-lg"
                    :name="values.phone_is_visible ? 'visibility' : 'visibility_off'"
                />
              </button>
            </div>
          </div>
        </div>

        <InputLocation
            id="location"
            v-model:value="values.location"
            v-model:errors="errors.location"
            :label-country="__('Country')"
            :label-city="__('City')"
            :placeholder-country="__('Select country')"
            :placeholder-city="__('Select city')"
            :options="props.options.country"
            class="md:col-span-2 mb-4"
            :disabled="isFieldDisabled('location')"
            required
        />

        <div class="grid gap-4 md:grid-cols-1">
          <InputText
              id="address"
              v-model:value="values.address"
              v-model:errors="errors.address"
              :label="__('Company Address')"
              :placeholder="__('Add Company Address')"
              :disabled="isFieldDisabled('address')"
              required
          />
<!--          <InputText-->
<!--              id="cui"-->
<!--              v-model:value="values.cui"-->
<!--              v-model:errors="errors.cui"-->
<!--              :label="__('CUI')"-->
<!--              :placeholder="__('Add Company CUI')"-->
<!--              :disabled="isFieldDisabled('cui')"-->
<!--              required-->
<!--          />-->
        </div>

        <div
            class="mt-8 flex flex-col-2 md:flex-col gap-2 md:flex-row-reverse justify-center items-center"
        >
          <Button
              class="flex items-center justify-center gap-1.5"
              :type="isLoading || !isAnyFieldEditable ? 'button' : 'submit'"
              :disabled="isLoading || !isAnyFieldEditable"
          >
            <Icon
                v-if="isLoading"
                class="animate-spin text-lg"
                name="progress_activity"
            />
            <span>{{ isLoading ? __('Loading...') : __('Save') }}</span>
          </Button>

          <Button
              variant="ghost"
              @click="handleCancelButtonClick"
              :disabled="!isAnyFieldEditable"
          >
            <span>{{ __('Cancel') }}</span>
          </Button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import {computed, onMounted, reactive, ref} from 'vue';
  import { useToast } from 'vue-toastification';
  import { __ } from '@/helpers';
  import { Button } from '@/components/block/button';
  import { Icon } from '@/components/block/icon';
  import { SectionHeader } from '@/pages/components/section-header';
  import axios from "axios";
  import {InputText} from "../../../../../components/form/input-text";
  import {InputLocation} from "../../../../../components/form/input-location";
  import {InputSelect} from "../../../../../components/form/input-select";

  const props = defineProps({
    profile: {
      type: Object,
      required: true
    },
    options: {
      type: Object,
      required: true
    },
    companyLocation: {
      type: Object,
      required: true
    },
    vettingOn: {
      type: Boolean,
      required: true
    },
    companyPhoneCode: {
      type: [Object,null],
      required: true
    },
    isCompanyOwner: {
      type: Boolean,
      required: true
    }
  });

  const toast = useToast();
  const emits = defineEmits(['save', 'cancel']);

  const values = ref({
    name: props.profile.company.name,
    website: props.profile.company.website,
    email: props.profile.company.email || '',
    email_is_visible: props.profile.company.email_is_visible ?? true,
    address: props.profile.company.address,
    phone_code: props.companyPhoneCode || null,
    phone_number: props.profile.company.phone_number || '',
    phone_is_visible: props.profile.company.phone_is_visible ?? true,
    location: props.companyLocation || null,
    cui: props.profile.company.cui || null,
  });

  const errors = ref({
    name: [],
    website: [],
    email: [],
    email_is_visible: [],
    address: [],
    phone_code: [],
    phone_number: [],
    phone_is_visible: [],
    location: [],
    cui: []
  });

  const isLoading = ref(false);

  const isFieldDisabled = (field) => {
    if (!props.isCompanyOwner) return true;
    return props.vettingOn && lockedFields[field];
  };

  const isAnyFieldEditable = computed(() => {
    if (!props.vettingOn) return true;
    return Object.keys(values.value).some(key => !lockedFields[key]);
  });

  const lockedFields = reactive({});

  function handleCancelButtonClick() {
    emits('cancel');
  }

  onMounted(() => {
    if (props.vettingOn) {
      for (const key in values.value) {
        if (key === 'location') {
          lockedFields.location = !!values.value.location.city?.id;
        } else {
          lockedFields[key] = !!values.value[key];
        }
      }
    }
  });

  function handleVisibilityButtonClick(key) {
    if (key === 'email') {
      values.value.email_is_visible = !values.value.email_is_visible;
    } else if (key === 'phone_number') {
      values.value.phone_is_visible = !values.value.phone_is_visible;
    }
  }

async function handleSubmit() {
    const requiredFieldKeys = [
        'name',
        'website',
        'address',
        'phone_code',
        'phone_number',
        'location'
    ];
    let firstElement;

    requiredFieldKeys.forEach((key) => {
      if (!values.value[key]) {
        errors.value[key] = [__('This field is required')];

        const element = document.getElementById(key);

        if (element && !firstElement) {
          firstElement = element;
        }
      }
    });

    if (
        values.value.location &&
        values.value.location.country &&
        !values.value.location.city &&
        !isFieldDisabled('location')
    ) {
      errors.value.location = [__('This field is required')];

      const element = document.getElementById('location');

      if (element && !firstElement) {
        firstElement = element;
      }
    }

    if (Object.values(errors.value).some((error) => error.length)) {
      if (firstElement) {
        firstElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return;
    }

    isLoading.value = true;

    try {
      console.log(values.value);
      const response = await axios.post(
        route('redesign.company.recruiter.update-company-details'),
        values.value
      );

      if (response?.data?.message) {
        toast.success(response.data.message);
      }
      if (props.vettingOn) {
        for (const key in values.value) {
          if (values.value[key]) {
            lockedFields[key] = true;
          }
        }
      }
    } catch (error) {
      if (error?.response?.data?.errors) {
        errors.value = error.response.data.errors;
      } else {
        toast.error(
          __('An unexpected error has occured. Please try again later.')
        );
      }
    }

    isLoading.value = false;
  }
</script>
